import type { Data as PuckData } from "@measured/puck";

export interface PuckTemplateVersionSummary {
  rowId: number;
  versionId: string;
  versionNumber: number;
  commitMessage: string | null;
  createdUtc: string;
  createdByUserId: string;
  isLive: boolean;
  tags: string[];
  configVersion: string;
  dataAdapterVersion: string;
}

export interface MacroEditorState {
  templateKey: string;
  templateId: string;
  liveVersionId: string | null;
  activeVersion: PuckTemplateVersionSummary | null;
  hasUnpublishedChanges: boolean;
  versions: PuckTemplateVersionSummary[];
  pageData: PuckData;
}

export interface RoutingUsage {
  source: string;
  branch: string;
}

export interface MacroTemplateSummary {
  templateKey: string;
  templateId: string;
  name: string;
  slug: string;
  type: string;
  description: string;
  hasPublishedVersion: boolean;
  routingUsages: RoutingUsage[];
}

export interface SaveMacroVersionRequest {
  pageData: PuckData;
  commitMessage?: string;
  publish: boolean;
  saveAsNewDraft: boolean;
}

export interface CreateMacroRequest {
  name: string;
  slug: string;
  type: string;
  description?: string;
}

const API_BASE = "/api/sales/macro-puck";

// Macro types available for selection
export const MACRO_TYPES = [
  { label: "Contact Form Quote", value: "ContactFormQuote" },
  { label: "Contact Form Auto Reply", value: "ContactFormAutoReply" },
  { label: "Booking Confirmation", value: "BookingConfirmation" },
  { label: "Payment Link", value: "PaymentLink" },
] as const;

export type MacroType = (typeof MACRO_TYPES)[number]["value"];

/**
 * Generate template key from slug
 */
export function macroTemplateKey(slug: string): string {
  return `@MACROS/${slug.toUpperCase()}`;
}

/**
 * Extract slug from template key
 */
export function slugFromTemplateKey(templateKey: string): string {
  return templateKey.replace(/^@MACROS\//, "").toLowerCase();
}

/**
 * Load all macro templates
 */
export async function loadMacroTemplates(): Promise<MacroTemplateSummary[]> {
  const resp = await fetch(`${API_BASE}/templates`, {
    credentials: "include",
  });
  if (!resp.ok) {
    throw new Error(`Failed to load macro templates (${resp.status})`);
  }
  return resp.json();
}

/**
 * Load editor state for a macro template
 */
export async function loadMacroEditorState(
  templateKey: string
): Promise<MacroEditorState> {
  const resp = await fetch(
    `${API_BASE}/templates/${encodeURIComponent(templateKey)}/editor-state`,
    {
      credentials: "include",
    }
  );
  if (!resp.ok) {
    throw new Error(`Failed to load macro (${resp.status})`);
  }
  return resp.json();
}

/**
 * Load editor state for a specific version
 */
export async function loadMacroEditorStateForVersion(
  templateKey: string,
  versionId: string
): Promise<MacroEditorState> {
  const resp = await fetch(
    `${API_BASE}/templates/${encodeURIComponent(templateKey)}/versions/${versionId}`,
    {
      credentials: "include",
    }
  );
  if (!resp.ok) {
    throw new Error(`Failed to load version (${resp.status})`);
  }
  return resp.json();
}

/**
 * Create a new macro template
 */
export async function createMacroTemplate(
  request: CreateMacroRequest
): Promise<MacroTemplateSummary> {
  const resp = await fetch(`${API_BASE}/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(request),
  });
  if (!resp.ok) {
    const error = await resp.text();
    throw new Error(`Failed to create macro (${resp.status}): ${error}`);
  }
  return resp.json();
}

/**
 * Save a new version of a macro template
 */
export async function saveMacroVersion(
  templateKey: string,
  request: SaveMacroVersionRequest
): Promise<MacroEditorState> {
  const resp = await fetch(
    `${API_BASE}/templates/${encodeURIComponent(templateKey)}/versions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(request),
    }
  );
  if (!resp.ok) {
    throw new Error(`Failed to save macro (${resp.status})`);
  }
  return resp.json();
}

/**
 * Publish a specific version of a macro template
 */
export async function publishMacroVersion(
  templateKey: string,
  versionId: string
): Promise<MacroEditorState> {
  const resp = await fetch(
    `${API_BASE}/templates/${encodeURIComponent(templateKey)}/versions/${versionId}/publish`,
    {
      method: "PUT",
      credentials: "include",
    }
  );
  if (!resp.ok) {
    throw new Error(`Failed to publish macro (${resp.status})`);
  }
  return resp.json();
}

/**
 * Delete a macro template
 */
export async function deleteMacroTemplate(templateKey: string): Promise<void> {
  const resp = await fetch(
    `${API_BASE}/templates/${encodeURIComponent(templateKey)}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );
  if (!resp.ok) {
    throw new Error(`Failed to delete macro (${resp.status})`);
  }
}

/**
 * Load published page data for a macro (for rendering)
 */
export async function loadMacroPageData(templateKey: string): Promise<PuckData> {
  const resp = await fetch(
    `${API_BASE}/templates/${encodeURIComponent(templateKey)}/page-data`,
    {
      credentials: "include",
    }
  );
  if (!resp.ok) {
    throw new Error(`Failed to load macro data (${resp.status})`);
  }
  return resp.json();
}

/**
 * Check if a macro slug already exists
 */
export async function checkMacroSlugExists(slug: string): Promise<boolean> {
  try {
    const templates = await loadMacroTemplates();
    const key = macroTemplateKey(slug);
    return templates.some(
      (t) => t.templateKey.toUpperCase() === key.toUpperCase()
    );
  } catch {
    return false;
  }
}

// --- Test Send APIs ---

export interface MacroBindingModelDto {
  FirstName?: string;
  ProgramDaysText?: string;
  ReservationId?: string;
  RoomCount?: number;
  TotalFitnessCount?: number;
  WeightLossCount?: number;
  QuoteLink?: string;
}

export interface TestSendZendeskRequest {
  templateKey: string;
  subject?: string;
  requesterEmail?: string;
  requesterName?: string;
  existingTicketId?: number | null;
  useFakeZendesk: boolean;
  bindingModel?: MacroBindingModelDto;
}

export interface TestSendZendeskResponse {
  success: boolean;
  ticketId: number;
  isNewTicket: boolean;
  message?: string;
}

export interface TestSendEmailRequest {
  templateKey: string;
  subject?: string;
  recipientEmail: string;
  recipientName?: string;
  bindingModel?: MacroBindingModelDto;
}

export interface TestSendEmailResponse {
  success: boolean;
  notificationId?: string;
  message?: string;
}

/**
 * Send a test Zendesk ticket with rendered macro content
 */
export async function testSendZendesk(
  request: TestSendZendeskRequest
): Promise<TestSendZendeskResponse> {
  const resp = await fetch(`${API_BASE}/test/zendesk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(request),
  });
  if (!resp.ok) {
    const error = await resp.text();
    throw new Error(`Zendesk test send failed (${resp.status}): ${error}`);
  }
  return resp.json();
}

/**
 * Send a test email with rendered macro content
 */
export async function testSendEmail(
  request: TestSendEmailRequest
): Promise<TestSendEmailResponse> {
  const resp = await fetch(`${API_BASE}/test/email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(request),
  });
  if (!resp.ok) {
    const error = await resp.text();
    throw new Error(`Email test send failed (${resp.status}): ${error}`);
  }
  return resp.json();
}

/**
 * Get the current user's email for pre-filling forms
 */
export async function getCurrentUserEmail(): Promise<string> {
  try {
    const resp = await fetch(`${API_BASE}/test/current-user-email`, {
      credentials: "include",
    });
    if (!resp.ok) return "";
    const data = await resp.json();
    return data.email ?? "";
  } catch {
    return "";
  }
}
